import { useEffect, useState } from 'react';
import { getApiErrorDetail, useFeatureFlags, useIsFeatureFlagActive } from '@klareospace/pharos-lib-front-utils';
import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import { useNavigate } from '@tanstack/react-router';

import { FEATURE_FLAGS } from '&/feature-flags';

import { IMPORT_TYPE_CONFIGS } from '../config';
import type { ImportType, ImportTypeConfig } from '../config';
import { useImportFile } from './use-import-file';

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_IMPORT_TYPE_CONFIG: ImportTypeConfig =
  IMPORT_TYPE_CONFIGS.find((config) => config.active) ?? IMPORT_TYPE_CONFIGS[0];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseImportPageReturn {
  error: ApiErrorDetail | null;
  isReady: boolean;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  onTypeChange: (type: ImportType) => void;
  options: readonly ImportTypeConfig[];
  selectedConfig: ImportTypeConfig;
  selectedType: ImportType;
}

/**
 * Composes the import page's guard + upload wiring:
 * - `useIsFeatureFlagActive` is fail-closed while flags are loading, so the redirect only fires
 *   once `useFeatureFlags()` has settled — otherwise a direct page load would bounce to /orders
 *   before the flags arrive.
 * - `isReady` is only `true` once the flags have settled AND the flag is active, so the page can
 *   avoid flashing its content before a pending redirect resolves.
 * - Owns the selected import type, defaulting to the first active entry in `IMPORT_TYPE_CONFIGS`.
 *   Switching type also resets the upload mutation so a stale error from the previous type does
 *   not survive the switch.
 * - Exposes the upload mutation's error as a plain `ApiErrorDetail` for the page to render in an
 *   `ErrorBanner` (the mutation itself does not toast on error — see `useImportFile`).
 */
export function useImportPage(): UseImportPageReturn {
  const navigate = useNavigate();
  const { isLoading: isFeatureFlagsLoading } = useFeatureFlags();
  const isFileImportActive = useIsFeatureFlagActive(FEATURE_FLAGS.FILE_IMPORT);
  const [selectedConfig, setSelectedConfig] = useState<ImportTypeConfig>(DEFAULT_IMPORT_TYPE_CONFIG);
  const importFile = useImportFile(selectedConfig);

  useEffect(() => {
    if (isFeatureFlagsLoading || isFileImportActive) return;
    void navigate({ replace: true, to: '/orders' });
  }, [isFeatureFlagsLoading, isFileImportActive, navigate]);

  function onFileSelected(file: File): void {
    importFile.mutate(file);
  }

  function onTypeChange(type: ImportType): void {
    const nextConfig = IMPORT_TYPE_CONFIGS.find((config) => config.type === type);
    if (!nextConfig) return;
    setSelectedConfig(nextConfig);
    importFile.reset();
  }

  return {
    error: importFile.error ? getApiErrorDetail(importFile.error) : null,
    isReady: !isFeatureFlagsLoading && isFileImportActive,
    isUploading: importFile.isPending,
    onFileSelected,
    onTypeChange,
    options: IMPORT_TYPE_CONFIGS,
    selectedConfig,
    selectedType: selectedConfig.type,
  };
}
